import React, { useState } from "react";
import { Button, Table } from "@/components";
import { Dialog } from "@/components/ui/dialog";
import type { Column } from "@/components";
import {
  ArrowLeft,
  Calendar,
  Target,
  Bot,
  Megaphone,
  Layers,
  User,
  BarChart3,
  TrendingUp,
  Eye,
  Share2,
  FileText,
  ChevronDown,
  ChevronRight,
  Pencil,
  Trash2,
  Sparkles,
} from "lucide-react";
import type {
  ContentStrategy,
  ContentStrategyWithDetails,
  ContentStrategyAgent,
  ContentStrategyBrand,
  ContentStrategyCampaign,
  ContentStrategyIntegration,
} from "../interfaces";

interface ResolvedEntities {
  agents: { id: number; name: string; imageUrl?: string }[];
  brands: { id: number; title: string; imageUrl?: string }[];
  campaigns: { id: number; title: string; imageUrl?: string }[];
  integrations?: { id: number; label: string }[];
}

interface StrategyDetailProps {
  strategy: ContentStrategy | ContentStrategyWithDetails;
  /** Fallback when strategy has IDs only (from list) - not needed when strategy has brands/campaigns/agents */
  resolvedEntities?: ResolvedEntities;
  onBack: () => void;
  onEdit?: () => void;
  onDelete?: (id: number) => void;
  isDeleting?: boolean;
  onActivate?: (id: number) => void;
  onDeactivate?: (id: number) => void;
  /** Called when user clicks Generate (video from this strategy). Only shown when strategy is ACTIVE. */
  onGenerate?: (strategyId: number) => void;
  isGenerating?: boolean;
}

function normalizeAgents(
  strategy: ContentStrategy | ContentStrategyWithDetails,
  resolvedEntities?: ResolvedEntities
): { id: number; name: string; imageUrl?: string }[] {
  const rich = strategy as ContentStrategyWithDetails;
  if (rich.agents && rich.agents.length > 0) {
    return rich.agents.map((a: ContentStrategyAgent) => ({
      id: a.id,
      name: a.name,
      imageUrl: a.images?.[0],
    }));
  }
  return resolvedEntities?.agents ?? [];
}

function normalizeBrands(
  strategy: ContentStrategy | ContentStrategyWithDetails,
  resolvedEntities?: ResolvedEntities
): { id: number; title: string; imageUrl?: string }[] {
  const rich = strategy as ContentStrategyWithDetails;
  if (rich.brands && rich.brands.length > 0) {
    return rich.brands.map((b: ContentStrategyBrand) => ({
      id: b.id,
      title: b.title,
      imageUrl: b.images?.[0],
    }));
  }
  return resolvedEntities?.brands ?? [];
}

function normalizeCampaigns(
  strategy: ContentStrategy | ContentStrategyWithDetails,
  resolvedEntities?: ResolvedEntities
): { id: number; title: string; imageUrl?: string }[] {
  const rich = strategy as ContentStrategyWithDetails;
  if (rich.campaigns && rich.campaigns.length > 0) {
    return rich.campaigns.map((c: ContentStrategyCampaign) => ({
      id: c.id,
      title: c.title,
      imageUrl: c.images?.[0],
    }));
  }
  return resolvedEntities?.campaigns ?? [];
}

function normalizeIntegrations(
  strategy: ContentStrategy | ContentStrategyWithDetails,
  resolvedEntities?: ResolvedEntities
): { id: number; label: string }[] {
  const rich = strategy as ContentStrategyWithDetails;
  if (rich.integrations && rich.integrations.length > 0) {
    return rich.integrations.map((i: ContentStrategyIntegration) => {
      const a = i.accountInfo;
      const label = a?.username
        ? `@${a.username}`
        : a?.pageName ??
          a?.displayName ??
          a?.channelTitle ??
          a?.name ??
          i.platform;
      return { id: i.id, label: `${i.platform} ${label}` };
    });
  }
  return resolvedEntities?.integrations ?? [];
}

// Dummy posts for strategy detail (similar to ContentReview)
const DUMMY_POSTS = [
  {
    id: 1,
    title: "Product Launch Announcement",
    description: "Exciting news! Our new AI-powered features are now live...",
    author: "Alex Chen",
    image:
      "https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=400&h=200&fit=crop",
    status: "pending",
    createdAt: "2 hours ago",
  },
  {
    id: 2,
    title: "Weekly Tips: Social Media Best Practices",
    description: "Learn how to optimize your social media presence...",
    author: "Jordan Smith",
    image:
      "https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?w=400&h=200&fit=crop",
    status: "approved",
    createdAt: "1 day ago",
  },
  {
    id: 3,
    title: "Customer Success Story",
    description:
      "How TechCorp increased engagement by 40% using our platform...",
    author: "Sam Wilson",
    image:
      "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400&h=200&fit=crop",
    status: "pending",
    createdAt: "3 hours ago",
  },
  {
    id: 4,
    title: "Holiday Campaign Teaser",
    description: "Get ready for our biggest promotion of the year...",
    author: "Morgan Lee",
    image:
      "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=200&fit=crop",
    status: "rejected",
    createdAt: "5 hours ago",
  },
];

function getEntityCounts(
  strategy: ContentStrategy | ContentStrategyWithDetails
) {
  const rich = strategy as ContentStrategyWithDetails;
  return {
    agents: rich.agents?.length ?? strategy.agentIds?.length ?? 0,
    brands: rich.brands?.length ?? strategy.brandIds?.length ?? 0,
    campaigns: rich.campaigns?.length ?? strategy.campaignIds?.length ?? 0,
    integrations:
      rich.integrations?.length ?? strategy.integrationIds?.length ?? 0,
  };
}

export const StrategyDetail: React.FC<StrategyDetailProps> = ({
  strategy,
  resolvedEntities,
  onBack,
  onEdit,
  onDelete,
  isDeleting = false,
  onActivate,
  onDeactivate,
  onGenerate,
  isGenerating = false,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const duration =
    strategy.config?.duration &&
    `${strategy.config.duration.periodCount} ${strategy.config.duration.period}`;

  const agents = normalizeAgents(strategy, resolvedEntities);
  const brands = normalizeBrands(strategy, resolvedEntities);
  const campaigns = normalizeCampaigns(strategy, resolvedEntities);
  const integrations = normalizeIntegrations(strategy, resolvedEntities);
  const counts = getEntityCounts(strategy);

  const [detailsExpanded, setDetailsExpanded] = useState(true);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          title="Back to list"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-2">
          {onGenerate &&
              strategy.status === "ACTIVE" && (
                <Button
                  size="sm"
                  onClick={() => onGenerate(strategy.id)}
                  disabled={isGenerating}
                  className="gap-2"
                >
                  <Sparkles className="h-4 w-4" />
                  {isGenerating ? "Generating…" : "Generate"}
                </Button>
              )}
            {onEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={onEdit}
                className="gap-2"
              >
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
            )}
          {onDelete && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDeleteConfirm(true)}
              className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
          )}
          <span
            className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
              strategy.status === "ACTIVE"
                ? "bg-green-500/10 text-green-600 dark:text-green-400"
                : "bg-muted text-muted-foreground"
            }`}
          >
            {strategy.status}
          </span>
          {onActivate &&
            onDeactivate &&
            (strategy.status === "ACTIVE" ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDeactivate(strategy.id)}
              >
                Deactivate
              </Button>
            ) : (
              <Button size="sm" onClick={() => onActivate(strategy.id)}>
                Activate
              </Button>
            ))}
        </div>
      </div>

      {/* Name & description */}
      <div className="space-y-1">
        <h2 className="text-2xl font-semibold text-foreground">
          {strategy.name}
        </h2>
        {strategy.description && (
          <p className="text-muted-foreground">{strategy.description}</p>
        )}
      </div>

      {/* Meta */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          <span>
            Created {new Date(strategy.createdAt).toLocaleDateString()}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          <span>
            Updated {new Date(strategy.updatedAt).toLocaleDateString()}
          </span>
        </div>
        <span className="px-2 py-1 bg-muted rounded text-xs font-medium">
          {strategy.mode}
        </span>
      </div>

      {/* Analytics placeholder */}
      <div className="bg-card border border-input rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="h-5 w-5" />
          <h3 className="font-semibold">Analytics</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="p-4 rounded-lg bg-muted/50">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <TrendingUp className="h-4 w-4" />
              <span>Posts Generated</span>
            </div>
            <p className="text-2xl font-semibold">—</p>
          </div>
          <div className="p-4 rounded-lg bg-muted/50">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <Eye className="h-4 w-4" />
              <span>Impressions</span>
            </div>
            <p className="text-2xl font-semibold">—</p>
          </div>
          <div className="p-4 rounded-lg bg-muted/50">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <Share2 className="h-4 w-4" />
              <span>Engagements</span>
            </div>
            <p className="text-2xl font-semibold">—</p>
          </div>
          <div className="p-4 rounded-lg bg-muted/50">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <BarChart3 className="h-4 w-4" />
              <span>Conversion Rate</span>
            </div>
            <p className="text-2xl font-semibold">—</p>
          </div>
        </div>
        <div className="h-48 rounded-lg bg-muted/30 flex items-center justify-center border border-dashed border-input">
          <p className="text-sm text-muted-foreground">
            Analytics charts will be displayed here
          </p>
        </div>
      </div>

      {/* Plan details (collapsible section) */}
      <div className="bg-card border border-input rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setDetailsExpanded((prev) => !prev)}
          className="flex w-full items-center justify-between gap-2 p-4 text-left hover:bg-muted/50 transition-colors"
        >
          <h3 className="font-semibold">Plan details</h3>
          {detailsExpanded ? (
            <ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
          )}
        </button>
        {detailsExpanded && (
          <div className="border-t border-input p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Duration */}
              {duration && (
                <div className="bg-muted/30 border border-input rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="h-5 w-5" />
                    <h4 className="font-semibold">Duration</h4>
                  </div>
                  <p className="text-muted-foreground">{duration}</p>
                </div>
              )}

              {/* Demographics */}
              {strategy.config?.demographics &&
                ((strategy.config.demographics.ageGroups?.length ?? 0) > 0 ||
                  (strategy.config.demographics.regions?.length ?? 0) > 0 ||
                  (strategy.config.demographics.ethnicities?.length ?? 0) > 0 ||
                  (strategy.config.demographics.interests?.length ?? 0) >
                    0) && (
                  <div className="bg-muted/30 border border-input rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <User className="h-5 w-5" />
                      <h4 className="font-semibold">Demographics</h4>
                    </div>
                    <dl className="grid gap-2 text-sm">
                      {strategy.config.demographics.ageGroups &&
                        strategy.config.demographics.ageGroups.length > 0 && (
                          <div>
                            <dt className="text-muted-foreground">
                              Age Groups
                            </dt>
                            <dd>
                              {strategy.config.demographics.ageGroups.join(
                                ", "
                              )}
                            </dd>
                          </div>
                        )}
                      {strategy.config.demographics.regions &&
                        strategy.config.demographics.regions.length > 0 && (
                          <div>
                            <dt className="text-muted-foreground">Regions</dt>
                            <dd>
                              {strategy.config.demographics.regions.join(", ")}
                            </dd>
                          </div>
                        )}
                      {strategy.config.demographics.ethnicities &&
                        strategy.config.demographics.ethnicities.length > 0 && (
                          <div>
                            <dt className="text-muted-foreground">
                              Ethnicities
                            </dt>
                            <dd>
                              {strategy.config.demographics.ethnicities.join(
                                ", "
                              )}
                            </dd>
                          </div>
                        )}
                      {strategy.config.demographics.interests &&
                        strategy.config.demographics.interests.length > 0 && (
                          <div>
                            <dt className="text-muted-foreground">Interests</dt>
                            <dd>
                              {strategy.config.demographics.interests.join(
                                ", "
                              )}
                            </dd>
                          </div>
                        )}
                    </dl>
                  </div>
                )}

              {/* Agents */}
              <div className="bg-muted/30 border border-input rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Bot className="h-5 w-5" />
                  <h4 className="font-semibold">Agents ({counts.agents})</h4>
                </div>
                {agents.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {agents.map((a) => (
                      <div
                        key={a.id}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50"
                      >
                        {a.imageUrl && (
                          <div className="w-8 h-8 rounded overflow-hidden shrink-0">
                            <img
                              src={a.imageUrl}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <span className="text-sm">{a.name}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No agents linked
                  </p>
                )}
              </div>

              {/* Brands */}
              <div className="bg-muted/30 border border-input rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Layers className="h-5 w-5" />
                  <h4 className="font-semibold">Brands ({counts.brands})</h4>
                </div>
                {brands.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {brands.map((b) => (
                      <div
                        key={b.id}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50"
                      >
                        {b.imageUrl && (
                          <div className="w-8 h-8 rounded overflow-hidden shrink-0">
                            <img
                              src={b.imageUrl}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <span className="text-sm">{b.title}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No brands linked
                  </p>
                )}
              </div>

              {/* Campaigns */}
              <div className="bg-muted/30 border border-input rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Megaphone className="h-5 w-5" />
                  <h4 className="font-semibold">
                    Campaigns ({counts.campaigns})
                  </h4>
                </div>
                {campaigns.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {campaigns.map((c) => (
                      <div
                        key={c.id}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50"
                      >
                        {c.imageUrl && (
                          <div className="w-8 h-8 rounded overflow-hidden shrink-0">
                            <img
                              src={c.imageUrl}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <span className="text-sm">{c.title}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No campaigns linked
                  </p>
                )}
              </div>

              {/* Integrations */}
              <div className="bg-muted/30 border border-input rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Share2 className="h-5 w-5" />
                  <h4 className="font-semibold">
                    Integrations ({counts.integrations})
                  </h4>
                </div>
                {integrations.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {integrations.map((i) => (
                      <div
                        key={i.id}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50 text-sm"
                      >
                        {i.label}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No integrations linked
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Posts (dummy table, similar to Content Review) */}
      <div className="bg-card border border-input rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="h-5 w-5" />
          <h3 className="font-semibold">Posts</h3>
        </div>
        <Table<{
          id: number;
          title: string;
          description: string;
          author: string;
          image: string;
          status: string;
          createdAt: string;
        }>
          columns={postsColumns}
          rows={DUMMY_POSTS}
          getRowId={(row) => row.id}
        />
      </div>

      {/* Delete confirmation */}
      <Dialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete content plan?"
        description={`This will permanently delete "${strategy.name}". This action cannot be undone.`}
        size="sm"
      >
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={isDeleting}
            onClick={() => onDelete?.(strategy.id)}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
};

type PostRow = (typeof DUMMY_POSTS)[0];

const postsColumns: Column<PostRow>[] = [
  {
    id: "image",
    label: "",
    minWidth: 80,
    format: (_, row) => (
      <div className="w-16 h-10 rounded overflow-hidden bg-muted shrink-0">
        <img src={row.image} alt="" className="w-full h-full object-cover" />
      </div>
    ),
  },
  { id: "title", label: "Title", minWidth: 160 },
  {
    id: "description",
    label: "Description",
    minWidth: 200,
    format: (val: string) => (
      <span className="text-sm text-muted-foreground line-clamp-2 max-w-[240px]">
        {val}
      </span>
    ),
  },
  { id: "author", label: "Author" },
  {
    id: "status",
    label: "Status",
    format: (val: string) => (
      <span
        className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
          val === "approved"
            ? "bg-green-500/10 text-green-600 dark:text-green-400"
            : val === "rejected"
            ? "bg-destructive/10 text-destructive"
            : "bg-amber-500/10 text-amber-600 dark:text-amber-400"
        }`}
      >
        {val}
      </span>
    ),
  },
  { id: "createdAt", label: "Created" },
];
